import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Play, Sparkles } from "lucide-react";

const AREA_NAMES = {
  neck: "Neck", shoulder: "Shoulder", upper_back: "Upper Back", lower_back: "Lower Back",
  wrist: "Wrist", elbow: "Elbow", hand: "Hand", hip: "Hip", other: "General",
};

export default function VideosPage() {
  const [data, setData] = useState({ library: [], recommended_areas: [] });
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/videos");
      setData(data);
    })();
  }, []);

  const recommended = data.library.filter((v) => data.recommended_areas.includes(v.area));
  const areas = Array.from(new Set(data.library.map((v) => v.area)));
  const shown = filter === "all" ? data.library : data.library.filter((v) => v.area === filter);

  return (
    <div>
      <span className="pill">Personalized Ergonomics Videos</span>
      <h1 className="mt-2 text-3xl font-bold">Move better. Scan longer.</h1>
      <p className="text-zinc-500 mt-1">Videos are personalized based on the pain regions you log.</p>

      {recommended.length > 0 && (
        <section className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5" style={{ color: "var(--gold)" }} />
            <h2 className="text-xl font-bold">Recommended for you</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {recommended.map((v) => <VideoCard key={v.id} v={v} />)}
          </div>
        </section>
      )}

      <section className="mt-10">
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <button onClick={() => setFilter("all")} className={`px-4 py-2 rounded-full text-sm font-semibold ${filter === "all" ? "bg-zinc-900 text-white" : "bg-white border border-zinc-200 text-zinc-700"}`} data-testid="videos-filter-all">All</button>
          {areas.map((a) => (
            <button key={a} onClick={() => setFilter(a)} className={`px-4 py-2 rounded-full text-sm font-semibold ${filter === a ? "bg-zinc-900 text-white" : "bg-white border border-zinc-200 text-zinc-700"}`} data-testid={`videos-filter-${a}`}>
              {AREA_NAMES[a] || a}
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {shown.map((v) => <VideoCard key={v.id} v={v} />)}
        </div>
      </section>
    </div>
  );
}

function VideoCard({ v }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden group hover:shadow-xl hover:-translate-y-0.5 transition-all" data-testid={`video-${v.id}`}>
      <div className="relative aspect-video bg-zinc-100">
        <img src={v.thumb} alt={v.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: "var(--gold)" }}>
            <Play className="w-6 h-6 text-white ml-1" />
          </span>
        </div>
        <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">{v.duration}</div>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between text-xs uppercase tracking-[0.15em] text-zinc-500 font-bold">
          <span>{AREA_NAMES[v.area] || v.area}</span>
          <span>{v.difficulty}</span>
        </div>
        <h3 className="mt-2 font-bold">{v.title}</h3>
        <p className="text-xs text-zinc-400 mt-2">Video content coming soon — preview only.</p>
      </div>
    </div>
  );
}
